/**
 * Pricing service - handles all pricing-related API operations
 */

import { BaseApiClient } from '../core/base-client';
import { 
  PriceCalculationResult, 
  PriceCalculationRequest,
  BookingType 
} from '../types';

export class PricingService {
  constructor(private client: BaseApiClient) {}

  /**
   * Calculate price for a workspace booking
   */
  async calculatePrice(request: PriceCalculationRequest): Promise<PriceCalculationResult> {
    const { workspaceId, fromDate, toDate, bookingType = BookingType.DAILY, amenities } = request;
    
    // If amenities are provided, use the POST endpoint
    if (amenities && amenities.length > 0) {
      const response = await this.client.post<{
        success: boolean;
        data: PriceCalculationResult;
        message: string;
      }>('/api/pricing/calculate', {
        workspaceId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        bookingType,
        amenities
      });

      return response.data;
    }
    
    // Otherwise, use the existing GET endpoint (backward compatibility)
    const queryParams = new URLSearchParams({
      fromDate: fromDate.toISOString(),
      toDate: toDate.toISOString(),
      bookingType
    });

    const response = await this.client.get<{
      success: boolean;
      data: PriceCalculationResult;
      message: string;
    }>(`/api/pricing/calculate/${workspaceId}?${queryParams.toString()}`);

    return response.data;
  }

  /**
   * Calculate price for a workspace booking (convenience method)
   */
  async calculateWorkspacePrice(
    workspaceId: string,
    fromDate: Date,
    toDate: Date,
    bookingType: BookingType = BookingType.DAILY
  ): Promise<PriceCalculationResult> {
    return this.calculatePrice({
      workspaceId,
      fromDate,
      toDate,
      bookingType
    });
  }
}

/**
 * Factory function to create pricing service
 */
export function createPricingService(client: BaseApiClient): PricingService {
  return new PricingService(client);
} 